import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2, ShoppingBag } from "lucide-react";

export default function Cart() {
  const nav = useNavigate();
  const { cart, updateCartQty, removeFromCart } = useApp();
  const [coupon, setCoupon] = useState("");
  const [quote, setQuote] = useState(null);

  const refresh = () => {
    if (cart.length === 0) return setQuote(null);
    api.post("/orders/quote", { items: cart, coupon_code: coupon }).then((r) => setQuote(r.data));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [cart, coupon]);

  if (cart.length === 0) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <ShoppingBag size={48} className="mx-auto text-muted-foreground" />
      <h1 className="font-display text-4xl font-black mt-6">Your bag is empty</h1>
      <p className="text-muted-foreground mt-2">Time to find something rare.</p>
      <Button data-testid="empty-shop-btn" onClick={() => nav("/men")} className="mt-8 bg-[#8B2E2E] hover:bg-[#6f2323]">Start shopping</Button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-[1fr_400px] gap-10">
      <div>
        <h1 className="font-display text-4xl font-black mb-8">Your Bag <span className="text-[#8B2E2E]">({cart.length})</span></h1>
        <div className="space-y-4">
          {cart.map((it, i) => (
            <div key={i} data-testid={`cart-item-${i}`} className="flex gap-4 border-b border-white/10 pb-4">
              <img src={it.image} alt={it.name} className="h-28 w-24 object-cover rounded-md" />
              <div className="flex-1">
                <p className="font-display text-lg">{it.name}</p>
                <p className="text-xs text-muted-foreground mt-1">Size {it.size}{it.color ? ` · ${it.color}` : ""}</p>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex items-center border border-white/20">
                    <button data-testid={`cart-dec-${i}`} onClick={() => updateCartQty(i, it.qty - 1)} className="w-8 h-8 hover:bg-white/5">−</button>
                    <span className="w-8 text-center text-sm font-semibold">{it.qty}</span>
                    <button data-testid={`cart-inc-${i}`} onClick={() => updateCartQty(i, it.qty + 1)} className="w-8 h-8 hover:bg-white/5">+</button>
                  </div>
                  <button data-testid={`cart-remove-${i}`} onClick={() => removeFromCart(i)} className="text-muted-foreground hover:text-[#D4B25C] transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="text-right font-semibold">{inr(it.price * it.qty)}</div>
            </div>
          ))}
        </div>
      </div>

      <aside className="lg:sticky lg:top-32 lg:self-start bg-[#2A231D] border border-[#D4B25C]/15 rounded-md p-6">
        <p className="label-tag mb-4">Order Summary</p>
        <div className="flex gap-2 mb-4">
          <Input data-testid="coupon-input" placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} />
          <Button data-testid="apply-coupon" variant="outline" onClick={refresh}>Apply</Button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Try: WELCOME10 · FLAT500 · VENVAY20</p>
        {quote && (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-sm"><span>Subtotal</span><span>{inr(quote.subtotal)}</span></div>
            {quote.b2g1_discount > 0 && <div className="flex justify-between text-sm text-[#D4B25C]"><span>Buy 2 Get 1 Free</span><span>−{inr(quote.b2g1_discount)}</span></div>}
            {quote.coupon_discount > 0 && <div className="flex justify-between text-sm text-[#D4B25C]"><span>Coupon ({coupon})</span><span>−{inr(quote.coupon_discount)}</span></div>}
            <div className="flex justify-between"><span>GST (5%)</span><span>{inr(quote.gst)}</span></div>
            <div className="flex justify-between font-bold text-xl border-t border-white/10 pt-3 mt-3"><span>Total</span><span data-testid="cart-total" className="text-[#D4B25C]">{inr(quote.total)}</span></div>
          </div>
        )}
        <Button data-testid="checkout-btn" onClick={() => nav("/checkout")} className="w-full mt-6 bg-[#0A0A0A] hover:bg-[#8B2E2E] h-12 uppercase tracking-widest text-xs">
          Proceed to checkout
        </Button>
        <button data-testid="continue-shopping" onClick={() => { toast.info("Keep browsing"); nav("/men"); }} className="w-full mt-3 text-xs uppercase tracking-widest font-semibold text-muted-foreground hover:text-[#8B2E2E] transition-colors">
          Continue shopping
        </button>
      </aside>
    </div>
  );
}
